import React from "react";
import { Plus, Minus } from "lucide-react";
import type { IGuestOptions } from "@/src/shared/features/search/types";

type GuestsModalProps = {
  filters: { guests: IGuestOptions };
  updateGuestCount: (type: keyof IGuestOptions, op: "add" | "subtract") => void;
  isAppliedValidation?: boolean;
  maxAdults?: number;
  maxChildren?: number;
  maxInfants?: number;
  showRooms?: boolean;
  maxRooms?: number;
  /** "app": the app's 40px square steppers (`CountStepperRow`) instead of round outlines. */
  stepper?: "round" | "app";
};

const GuestsModal: React.FC<GuestsModalProps> = ({
  filters,
  updateGuestCount,
  isAppliedValidation = false,
  maxAdults = Infinity,
  maxChildren = Infinity,
  maxInfants = Infinity,
  showRooms = false,
  maxRooms = Infinity,
  stepper = "round",
}) => {
  const config = [
    ...(showRooms
      ? [
          {
            type: "rooms" as keyof IGuestOptions,
            title: "Rooms",
            desc: "Number of rooms",
            min: 1,
            max: maxRooms,
          },
        ]
      : []),
    {
      type: "adults" as keyof IGuestOptions,
      title: "Adults",
      desc: "Ages 13 or above",
      min: 1,
      max: maxAdults,
    },
    {
      type: "children" as keyof IGuestOptions,
      title: "Children",
      desc: "Ages 2–12",
      min: 0,
      max: maxChildren,
    },
    {
      type: "infants" as keyof IGuestOptions,
      title: "Infants",
      desc: "Under 2",
      min: 0,
      max: maxInfants,
    },
  ];

  return (
    <>
      {config.map((item, idx) => {
        const rawValue = filters.guests[item.type];
        const currentValue =
          typeof rawValue === "number"
            ? rawValue
            : item.type === "rooms" || item.type === "adults"
              ? 1
              : 0;
        const disableSubtract = currentValue <= item.min;
        const disableAdd =
          isAppliedValidation && currentValue >= (item.max ?? Infinity);
        return (
          <div
            key={item.type}
            className={`flex justify-between items-center ${
              idx < config.length - 1
                ? "pb-6 mb-6 border-b border-gray-200"
                : ""
            }`}
          >
            <div>
              <div className="font-medium text-ink-black">{item.title}</div>
              <div className="text-sm text-ink-gray">{item.desc}</div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => updateGuestCount(item.type, "subtract")}
                disabled={disableSubtract}
                aria-label={`Fewer ${item.title.toLowerCase()}`}
                className={
                  stepper === "app"
                    ? "h-10 w-10 disabled:cursor-not-allowed disabled:opacity-40"
                    : "w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:border-gray-800 transition-colors"
                }
              >
                {stepper === "app" ? (
                  <img src="/icons/search/stepper_minus.svg" alt="" />
                ) : (
                  <Minus size={12} />
                )}
              </button>
              <span className="w-8 text-center text-ink-black font-medium">
                {currentValue}
              </span>
              <button
                type="button"
                onClick={() => updateGuestCount(item.type, "add")}
                disabled={disableAdd}
                aria-label={`More ${item.title.toLowerCase()}`}
                className={
                  stepper === "app"
                    ? "h-10 w-10 disabled:cursor-not-allowed disabled:opacity-40"
                    : "w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed hover:border-gray-800 transition-colors"
                }
              >
                {stepper === "app" ? (
                  <img src="/icons/search/stepper_plus.svg" alt="" />
                ) : (
                  <Plus size={12} />
                )}
              </button>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default GuestsModal;
